import React, { useState, useEffect } from 'react';
import { getAllBadges, getMyBadges, getBadgeLeaderboard } from '../services/badgeService';

const MyBadges = () => {
  const [allBadges, setAllBadges] = useState([]);
  const [myBadges, setMyBadges] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('my-badges'); // 'my-badges' or 'leaderboard'

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [badges, earnedBadges, board] = await Promise.all([
          getAllBadges(),
          getMyBadges(),
          getBadgeLeaderboard()
        ]);

        setAllBadges(badges);
        setMyBadges(earnedBadges);
        setLeaderboard(board);
      } catch (error) {
        console.error('Error fetching badges:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const earnedBadgeIds = new Set(myBadges.map(ub => ub.badge_id));

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center">Loading...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-10 px-4 sm:px-6 lg:px-8">
      <h1 className="text-4xl font-bold mb-8 gradient-text">🏆 Badges & Achievements</h1>

      {/* Tabs */}
      <div className="flex border-b-2 border-gray-200 mb-8">
        <button
          className={`py-3 px-6 font-medium text-base transition-all ${activeTab === 'my-badges' ? 'border-b-4 border-indigo-500 text-indigo-600 -mb-0.5' : 'text-gray-600 hover:text-gray-800'}`}
          onClick={() => setActiveTab('my-badges')}
        >
          My Badges ({myBadges.length})
        </button>
        <button
          className={`py-3 px-6 ml-4 font-medium text-base transition-all ${activeTab === 'leaderboard' ? 'border-b-4 border-indigo-500 text-indigo-600 -mb-0.5' : 'text-gray-600 hover:text-gray-800'}`}
          onClick={() => setActiveTab('leaderboard')}
        >
          Leaderboard
        </button>
      </div>

      {/* My Badges Tab */}
      {activeTab === 'my-badges' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {allBadges.map((badge) => {
            const isEarned = earnedBadgeIds.has(badge.id);
            const userBadge = myBadges.find(ub => ub.badge_id === badge.id);

            return (
              <div
                key={badge.id}
                className={`p-6 rounded-xl border-l-4 ${
                  isEarned
                    ? 'bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-500 shadow-md card-hover'
                    : 'bg-gray-50 border-gray-300 opacity-60'
                } fade-in transition-all`}
              >
                <div className="text-center">
                  <div className="text-6xl mb-3">{badge.icon_emoji || '🏅'}</div>
                  <h3 className="font-bold text-xl mb-2">{badge.name}</h3>
                  <p className="text-sm text-gray-600 mb-3">{badge.description}</p>
                  <div className="text-xs mb-2">
                    <span className={`px-3 py-1.5 rounded-full font-semibold ${
                      badge.tier === 'bronze' ? 'bg-gradient-to-r from-orange-400 to-orange-600 text-white' :
                      badge.tier === 'silver' ? 'bg-gradient-to-r from-gray-300 to-gray-500 text-white' :
                      badge.tier === 'gold' ? 'bg-gradient-to-r from-yellow-400 to-yellow-600 text-white' :
                      'bg-gradient-to-r from-purple-400 to-purple-600 text-white'
                    }`}>
                      {badge.tier.toUpperCase()}
                    </span>
                  </div>
                  {isEarned && userBadge && (
                    <div className="mt-3 text-sm font-medium text-green-600">
                      ✓ Earned {new Date(userBadge.earned_at).toLocaleDateString()}
                    </div>
                  )}
                  {!isEarned && (
                    <div className="mt-3 text-xs text-gray-500">
                      {badge.criteria_type === 'appreciations_received'
                        ? `Receive ${badge.criteria_threshold} appreciations`
                        : `Send ${badge.criteria_threshold} appreciations`
                      }
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Leaderboard Tab */}
      {activeTab === 'leaderboard' && (
        <div className="bg-white rounded-xl shadow-md card-hover fade-in">
          <div className="p-8">
            <h2 className="text-3xl font-bold mb-6 gradient-text">Top Badge Collectors</h2>
            <div className="space-y-3">
              {leaderboard.map((entry, index) => (
                <div
                  key={entry.user_id}
                  className={`flex items-center p-4 rounded-lg transition-all ${
                    index < 3
                      ? 'bg-gradient-to-r from-indigo-50 to-purple-50 border-l-4 border-indigo-500 shadow-sm'
                      : 'bg-gray-50 hover:bg-gray-100'
                  }`}
                >
                  <div className="text-3xl font-bold mr-5 w-12">
                    {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : `#${index + 1}`}
                  </div>
                  <div className="flex-1">
                    <div className="font-bold text-lg">{entry.name}</div>
                  </div>
                  <div className="text-2xl font-bold gradient-text">
                    {entry.badge_count} badge{entry.badge_count !== 1 ? 's' : ''}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyBadges;
